import React, { useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FileManager } from '@cubone/react-file-manager';
import '@cubone/react-file-manager/dist/style.css';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { fileExplorerService } from '../api/fileExplorerService.ts';
import { FileEntity } from "../types/entities/file.entity.ts";

const normalizePath = (p: string | null): string => {
  if (!p || p === '/' || p === 'null') return '/';
  const decoded = decodeURIComponent(p);
  return '/' + decoded.replace(/^\/+|\/+$/g, '');
};

export const FileExplorer = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPath = searchParams.get('path');
  const currentPathFromUrl = normalizePath(urlPath);

  const [path, setPath] = React.useState<string>(currentPathFromUrl);

  useEffect(() => {
    setPath(currentPathFromUrl);
  }, [currentPathFromUrl]);

  const { data = [], isLoading, refetch } = useQuery<FileEntity[]>({
    queryKey: ['files-tree', path],
    queryFn: () => fileExplorerService.getAll(path),
    staleTime: Infinity,
  });

  const handleFolderChange = (newPath: string) => {
    const normalized = normalizePath(newPath);

    setPath(normalized);

    if (normalized === '/') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ path: normalized }, { replace: true });
    }
  };

  const deleteFileMutation = useMutation({
    mutationFn: fileExplorerService.delete,
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      console.log(error)
    }
  });

  const handleOnDelete = (files: FileEntity[]) => {
    deleteFileMutation.mutate(files);
  }

  const createFolderMutation = useMutation({
    mutationFn: fileExplorerService.addFolder,
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      console.log(error)
    }
  });

  const handleCreateFolder = (name: string, parentFolder: FileEntity|null) => {
    const path = parentFolder ? `${parentFolder.path}/` : '/';
    createFolderMutation.mutate(`${path}${name}`);
  }

  const pasteMutation = useMutation({
    mutationFn: fileExplorerService.paste,
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      console.log(error)
    }
  });

  const handlePaste = (files: FileEntity[], newFolder: FileEntity, action: string) => {
    pasteMutation.mutate({ files, newFolder, action });
  }

  const renameMutation = useMutation({
    mutationFn: fileExplorerService.rename,
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      console.log(error)
    }
  });

  const handleRename = (file: FileEntity, newName: string) => {
    renameMutation.mutate({ file, newName });
  }

  const openMutation = useMutation({
    mutationFn: fileExplorerService.preview,
    onSuccess: (link: string) => {
      window.open(link, '_blank');
    },
    onError: (error) => {
      console.log(error)
    }
  });

  const handleFileOpen = (file: FileEntity) => {
    if (!file.isDirectory) {
      openMutation.mutate(file);
    }
  }

  const handleUploading = (_file: FileEntity, parentFolder: FileEntity) => {
    return {
      path: parentFolder?.path || '/'
    };
  };


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={68} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <FileManager
        files={data}
        initialPath={path}
        onFolderChange={handleFolderChange}
        onRefresh={() => {
          void refetch();
        }}
        onDelete={handleOnDelete}
        onCreateFolder={handleCreateFolder}
        onPaste={handlePaste}
        onRename={handleRename}
        permissions={{
          download: false
        }}
        enableFilePreview={false}
        onFileOpen={handleFileOpen}
        fileUploadConfig={{
          url: fileExplorerService.getUploadUrl(),
          method: 'POST',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('accessToken') }
        }}
        onFileUploading={handleUploading}
        onFileUploaded={() => {
          void refetch();
        }}
      />
    </Box>
  );
};
